/**
 * LEXI Oracle System - Deployed Contract Addresses
 * Network: Base Sepolia Testnet (Chain ID: 84532)
 * Deployment Date: October 23, 2025
 * Deployer: 0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6 (Living Arcade Wallet)
 */

export const CHAIN_ID = {
  BASE_SEPOLIA: 84532,
  BASE_MAINNET: 8453,
} as const;

/**
 * Deployed Contract Addresses on Base Sepolia
 * UPDATED: October 23, 2025 - Comprehensive V2 Deployment
 */
export const CONTRACTS = {
  [CHAIN_ID.BASE_SEPOLIA]: {
    // Core System Contracts (V2 Fresh Deployment)
    LEXI_CORE: '0xe2bd03e37849f6477ee3954286959b3cfe05809d' as `0x${string}`, // LexiCoreV2
    LEXI_TOKEN: '0x2D4A0E271e6207fB1F51C00A5F3ed64b9D1d114B' as `0x${string}`,
    GOVERNANCE: '0x2AE199df0BC4C2e3DaF8F8618A4001a6A6f3a8D7' as `0x${string}`,
    
    // Identity Oracle (NEW! - LEXI's self-awareness)
    IDENTITY_ORACLE: '0x9aaf1ef99da234c5c2ce4ff760484d4e273a96f7' as `0x${string}`,
    
    // Domain Oracles (Fresh V2 Deployment)
    GEOGRAPHY_ORACLE: '0x38858bc58807a0528e27b90b5d4b2cf958b9bf68' as `0x${string}`,
    PHILOSOPHY_ORACLE: '0xd62028f94478b58858d9bf3d0ffeac3d5462ad25' as `0x${string}`,
    SCIENCE_ORACLE: '0xe974e3fd7be41946e62ca2e97dddc7b7f616349e' as `0x${string}`,
    HISTORY_ORACLE: '0xf707bd21519417b6d63a825228f0b172534af08c' as `0x${string}`,
    ARTS_ORACLE: '0xfb43c40e147c41846eebc9b0889c7a6d7876e75e' as `0x${string}`,
    TECHNOLOGY_ORACLE: '0x80061a7b02c61197930985c94de32a12e9daac94' as `0x${string}`,
    
    // Test Token (Vibepool)
    VIBEPOOL_TOKEN: '0xB399B22037067b4A19fc6e5738C4918e6fE02Ec7' as `0x${string}`,
    
    // LEXI Wallet Address (for vote fees)
    LEXI_WALLET: '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6' as `0x${string}`,
    
    // Fact Registry (NEW! - Centralized fact governance)
    FACT_REGISTRY: '0x47fb69ed6861aab46a7ab3b4eda763a37608e1c1' as `0x${string}`,
  },
  [CHAIN_ID.BASE_MAINNET]: {
    // Mainnet Contract Addresses
    LEXI_CORE: '0xe2bd03e37849f6477ee3954286959b3cfe05809d' as `0x${string}`,
    LEXI_TOKEN: '0xEeB2e590B1D3b02F8de8A94c24E03dcD2CBDFB07' as `0x${string}`,
    FEE_MANAGER: '0xa59291df3bb8f19a162cf843321972fb7b689114' as `0x${string}`,
    FACT_REGISTRY: '0x47fb69ed6861aab46a7ab3b4eda763a37608e1c1' as `0x${string}`,
    
    // Oracle Contracts
    IDENTITY_ORACLE: '0x9aaf1ef99da234c5c2ce4ff760484d4e273a96f7' as `0x${string}`,
    GEOGRAPHY_ORACLE: '0x38858bc58807a0528e27b90b5d4b2cf958b9bf68' as `0x${string}`,
    PHILOSOPHY_ORACLE: '0xd62028f94478b58858d9bf3d0ffeac3d5462ad25' as `0x${string}`,
    SCIENCE_ORACLE: '0xe974e3fd7be41946e62ca2e97dddc7b7f616349e' as `0x${string}`,
    HISTORY_ORACLE: '0xf707bd21519417b6d63a825228f0b172534af08c' as `0x${string}`,
    ARTS_ORACLE: '0xfb43c40e147c41846eebc9b0889c7a6d7876e75e' as `0x${string}`,
    TECHNOLOGY_ORACLE: '0x80061a7b02c61197930985c94de32a12e9daac94' as `0x${string}`,
    
    // Legacy (for compatibility)
    GOVERNANCE: '0x0000000000000000000000000000000000000000' as `0x${string}`,
    VIBEPOOL_TOKEN: '0x0000000000000000000000000000000000000000' as `0x${string}`,
    LEXI_WALLET: '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6' as `0x${string}`,
  },
} as const;

/**
 * Vote and Proposal Costs (in VIBE tokens for testnet, USD for mainnet)
 */
export const VOTE_COSTS = {
  // Testnet costs (VIBE tokens)
  FACT_VOTE: '10', // 10 VIBE tokens per fact vote
  GOVERNANCE_VOTE: '50', // 50 VIBE tokens per governance vote
  PROPOSE_ORACLE: '100', // 100 VIBE tokens to propose new oracle
  PROPOSE_FACT: '5', // 5 VIBE tokens to propose new fact
} as const;

/**
 * Mainnet Fee Configuration (USD amounts)
 */
export const MAINNET_FEES = {
  CHAT_FEE_USD: 0.01, // $0.01 USD per chat query
  FACT_SUBMISSION_FEE_USD: 1.00, // $1.00 USD per fact proposal
  VOTING_FEE_USD: 0.25, // $0.25 USD per vote
  MIN_STAKE_LEXI: '100000000000000000000', // 100 LEXI tokens minimum stake
} as const;

/**
 * Chainlink Price Feed Configuration
 */
export const CHAINLINK_CONFIG = {
  [CHAIN_ID.BASE_MAINNET]: {
    LEXI_USD_PRICE_FEED: '0x4aDC67696bA383F43DD60A9e78F2C97Fbbfc7cb1', // Example address - update with actual
    FALLBACK_PRICE_USD: 100000000, // $1.00 (8 decimals)
  },
} as const;

/**
 * Domain Hashes (keccak256 of domain names) - V2 Deployment
 */
export const DOMAIN_HASHES = {
  IDENTITY: '0x5e1bbe66f8fb0284e8bef5f1c83d2b67a349062a7cdcb4fd2e5a3b0c72bd9fa3' as `0x${string}`,
  GEOGRAPHY: '0x79efabf9ee84f16d0f5e22036dede27d86fc02a9d64c7b7d69c0ac2eb0bd2179' as `0x${string}`,
  PHILOSOPHY: '0x55c2f1c25ca0e34e18eb5e807071b2b57f4a7c8f2e1fd094bac7766d6a4c64af' as `0x${string}`,
  SCIENCE: '0x60c5fbfaefb86b0f8f04d18c2130a9a28e78e30a0ced0e5f7cf13a7b7f6ccb8c' as `0x${string}`,
  HISTORY: '0x5cefa1e9b08b2c7a5efb78bb9f6038dc8f8b8e45fe22d48ce72ecb6ff4cb2cd8' as `0x${string}`,
  ARTS: '0xe2aea5dff0f5c0e7d02c72fc68ac6607d21e5f27e66ae02a66fcc0d52c1b6fd1' as `0x${string}`,
  TECHNOLOGY: '0xec3908fb9e278a449d4dcf84b5e5b5cb2c61b867cb5bce8e2925f48e9c57645' as `0x${string}`,
} as const;

/**
 * BaseScan Explorer URLs
 */
export const EXPLORER_URLS = {
  [CHAIN_ID.BASE_SEPOLIA]: 'https://sepolia.basescan.org',
  [CHAIN_ID.BASE_MAINNET]: 'https://basescan.org',
} as const;

/**
 * RPC URLs
 */
export const RPC_URLS = {
  [CHAIN_ID.BASE_SEPOLIA]: 'https://sepolia.base.org',
  [CHAIN_ID.BASE_MAINNET]: 'https://mainnet.base.org',
} as const;

/**
 * Helper function to get contract addresses for current chain
 */
export function getContracts(chainId: number) {
  return CONTRACTS[chainId as keyof typeof CONTRACTS] || CONTRACTS[CHAIN_ID.BASE_SEPOLIA];
}

/**
 * Helper function to get explorer URL for current chain
 */
export function getExplorerUrl(chainId: number) {
  return EXPLORER_URLS[chainId as keyof typeof EXPLORER_URLS] || EXPLORER_URLS[CHAIN_ID.BASE_SEPOLIA];
}

/**
 * Helper function to build transaction URL
 */
export function getTxUrl(chainId: number, txHash: string) {
  return `${getExplorerUrl(chainId)}/tx/${txHash}`;
}

/**
 * Helper function to build address URL
 */
export function getAddressUrl(chainId: number, address: string) {
  return `${getExplorerUrl(chainId)}/address/${address}`;
}

/**
 * Helper function to build token URL
 */
export function getTokenUrl(chainId: number, tokenAddress: string) {
  return `${getExplorerUrl(chainId)}/token/${tokenAddress}`;
}

/**
 * Helper function to get fee configuration for current chain
 */
export function getFeeConfig(chainId: number) {
  if (chainId === CHAIN_ID.BASE_MAINNET) {
    return {
      type: 'usd' as const,
      chatFee: MAINNET_FEES.CHAT_FEE_USD,
      factSubmissionFee: MAINNET_FEES.FACT_SUBMISSION_FEE_USD,
      votingFee: MAINNET_FEES.VOTING_FEE_USD,
      minStake: MAINNET_FEES.MIN_STAKE_LEXI,
    };
  } else {
    return {
      type: 'vibe' as const,
      chatFee: 0, // No fee for testnet chat
      factSubmissionFee: parseFloat(VOTE_COSTS.PROPOSE_FACT),
      votingFee: parseFloat(VOTE_COSTS.FACT_VOTE),
      minStake: '0', // No minimum stake for testnet
    };
  }
}

/**
 * Helper function to check if dynamic fees are enabled
 */
export function isDynamicFeesEnabled(chainId: number) {
  return chainId === CHAIN_ID.BASE_MAINNET;
}

/**
 * Helper function to get LEXI token address for current chain
 */
export function getLexiTokenAddress(chainId: number) {
  const contracts = getContracts(chainId);
  return contracts.LEXI_TOKEN;
}

/**
 * Oracle Information - V2 with Identity Oracle
 */
export const ORACLE_INFO = {
  identity: {
    name: 'Identity',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].IDENTITY_ORACLE,
    domain: DOMAIN_HASHES.IDENTITY,
    description: "LEXI's self-awareness and meta-knowledge about Living Arcade",
    icon: '🤖',
    factCount: 35, // Pre-populated with identity facts
    priority: 100, // Highest priority - checked first
  },
  geography: {
    name: 'Geography',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].GEOGRAPHY_ORACLE,
    domain: DOMAIN_HASHES.GEOGRAPHY,
    description: 'Geographic facts about countries, cities, and landmarks',
    icon: '🌍',
    priority: 80,
  },
  philosophy: {
    name: 'Philosophy',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].PHILOSOPHY_ORACLE,
    domain: DOMAIN_HASHES.PHILOSOPHY,
    description: 'Philosophical concepts and Living Arcade artistic philosophy',
    icon: '🧠',
    priority: 90,
  },
  science: {
    name: 'Science',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].SCIENCE_ORACLE,
    domain: DOMAIN_HASHES.SCIENCE,
    description: 'Scientific facts, discoveries, and natural phenomena',
    icon: '🔬',
    priority: 85,
  },
  history: {
    name: 'History',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].HISTORY_ORACLE,
    domain: DOMAIN_HASHES.HISTORY,
    description: 'Historical events, cultures, and civilizations',
    icon: '📜',
    priority: 75,
  },
  arts: {
    name: 'Arts',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].ARTS_ORACLE,
    domain: DOMAIN_HASHES.ARTS,
    description: 'Literature, creative arts, and cultural expressions',
    icon: '🎨',
    priority: 70,
  },
  technology: {
    name: 'Technology',
    address: CONTRACTS[CHAIN_ID.BASE_SEPOLIA].TECHNOLOGY_ORACLE,
    domain: DOMAIN_HASHES.TECHNOLOGY,
    description: 'Blockchain, Web3, and technological innovations',
    icon: '💻',
    priority: 80,
  },
} as const;

/**
 * Contract Deployment Info - V2 Comprehensive Deployment
 */
export const DEPLOYMENT_INFO = {
  network: 'Base Sepolia',
  chainId: CHAIN_ID.BASE_SEPOLIA,
  deploymentDate: '2025-10-23',
  deployer: '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6',
  version: 'V2',
  deploymentType: 'Fresh (not upgrade)',
  broadcastFile: '/Users/aaronvick/GIT/Lexis/broadcast/ComprehensiveDeployment.s.sol/84532/run-latest.json',
  features: [
    'LexiCoreV2 with askLexi() intelligence',
    'Identity Oracle (35 self-awareness facts)',
    '7 total oracles (Identity + 6 domains)',
    'Multi-oracle synthesis (up to 3 oracles)',
    'Confidence scoring (0-100%)',
    'Conversation memory (100 entries)',
    'Living Arcade philosophy integration',
    'Domain priority routing',
  ],
} as const;

